import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import {
	Toolbar as RhombusToolbar,
	Button,
	ToggleFilter
} from "rhombus-react";

import { AssetUploadContext } from "context/AssetUploadContext";
import { Assets } from "reducers/Assets";

import Permissioned from "widgets/Permissioned";
import SelectCount from 'widgets/toolbars/SelectCount';
import { SEARCH_DEBOUNCE, TYPES } from "utils/constants";

import { TOGGLE_VIEWS } from "./Helpers";
import fileUploadActions from "./FileUploadActions";
import BulkDownloadAssets from "widgets/toolbars/BulkDownloadAssets";
import BulkActionsButton from "routes/assets/BulkActionsButton";

import "./Toolbar.css";

const toggles = Object.values(TOGGLE_VIEWS);

export default function Toolbar({ searchTerm, handleSearch }){
	const dispatch = useDispatch()
		, {
			columnFilters,
			currentSelection,
			libraryId,
			libraryName,
			hasAE,
		} = useSelector((state) => {
			return {
				currentSelection:state.selected.assets,
				columnFilters:state.assets.columnFilters.active,
				libraryId:state.libraryProperties.id,
				libraryName:state.libraryProperties.name,
				hasAE:state.advertiserDetails?.data?.isAfterEffectsEnabled,
			};
		})
		, selectedAssetTypes = columnFilters.assetType.params || []
		, isSomethingSelected = (currentSelection.length > 0)
		, searchProps = {
			isDisabled: isSomethingSelected,
		}
		, allowedToggles = useMemo(() => {
			if(hasAE){ return toggles; }

			return toggles.filter(v => v.value !== "AfterEffects");
		}, [hasAE])
		;

	const { isValidating } = useContext(AssetUploadContext);

	let $buttons = [
		<Permissioned key="upload" domain="Creative View" action="Upload Assets">
			<Button
				icon="fas fa-cloud-upload-alt"
				style="block"
				isDropDown
				splitActions={fileUploadActions}
				disabled={isValidating}
			>
				Upload
			</Button>
		</Permissioned>
	];

	if(isSomethingSelected){
		$buttons.push([
			<div key="divider" className="toolbar-divider" />,
			<SelectCount key="selected" type={TYPES.ASSETS} />,
			<BulkDownloadAssets
				key="download-assets"
				libraryId={libraryId}
				libraryName={libraryName}
			/>,
			<BulkActionsButton key="bulk-actions" libraryId={libraryId} />,
		]);
	}

	function updateAssetTypeFilter(level, enabled){
		let selected;
		if(!enabled){ // Remove `level` from params if disabling
			selected = selectedAssetTypes.filter(type => (type !== level));
		} else { // Enabling.  Add `level` to params if it's not already in there.
			if(!selectedAssetTypes.includes(level)){
				selected = [...selectedAssetTypes, level];
			}
		}

		dispatch(Assets.updateColumnFilter("assetType", { params: selected }));
	}

	return (
		<RhombusToolbar
			className="asset-toolbar"
			search={true}
			searchProps={searchProps}
			searchDebounce={SEARCH_DEBOUNCE}
			onSearch={handleSearch}
			searchValue={searchTerm}>
			<div className="action-button-side flex-center">{$buttons}</div>
			<ToggleFilter
				iconsOnly
				disabled={isSomethingSelected}
				values={allowedToggles}
				selectedView = {selectedAssetTypes}
				onViewChange = {updateAssetTypeFilter}
			/>
		</RhombusToolbar>
	);
}

Toolbar.displayName = "AssetToolbar";
Toolbar.propTypes = {
	handleSearch:PropTypes.func.isRequired,
};
